import React, { useState, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/Sports_Schedule.css";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext.js";
import FullPageLoader from "../../components/ui/fullPageLoader.js";
import ChatbotButton from "../../components/common/ChatbotButton";
import { Sparkles, ArrowRight } from "lucide-react";

// --- 1. Smart Image Component (حل مشكلة الصور) ---
const SmoothImage = ({ src, alt }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="sport-img-container">
      {/* Skeleton Loader: يظهر طالما الصورة لسه متحملتش */}
      {!isLoaded && <div className="img-skeleton"></div>}

      <img
        src={src}
        alt={alt}
        className={`sport-img-advanced ${isLoaded ? "loaded" : ""}`}
        onLoad={() => setIsLoaded(true)} // لما تتحمل نخليها تظهر
        loading="lazy" // تحسين الأداء
      />
    </div>
  );
};

export default function Sports_Schedule() {
  const navigate = useNavigate();
  const { loading } = useContext(AuthContext);

  // تم تحديث المصفوفة لتشمل اللغتين (عربي - إنجليزي)
  const sportsPlaces = [
    {
      id: "football",
      titleAr: "ملعب كرة القدم",
      titleEn: "Football Field",
      descriptionAr:
        "احجز الملعب المعشب باحترافية. مثالي لمباريات الخماسي مع فريقك.",
      descriptionEn:
        "Reserve the professional turf field. Perfect for 5v5 matches with your squad.",
      image: "/Sports/Football.jpeg",
    },
    {
      id: "basketball",
      titleAr: "صالة كرة السلة",
      titleEn: "Basketball Hall",
      descriptionAr:
        "احجز الصالة المغطاة بأرضية ممتازة. مثالية للمباريات التنافسية أو التدريب.",
      descriptionEn:
        "Book the indoor court with premium flooring. Ideal for competitive games or practice.",
      image: "/Sports/BasketballHall.jpeg",
    },
    {
      id: "volleyball",
      titleAr: "ملعب الكرة الطائرة",
      titleEn: "Volleyball Court",
      descriptionAr:
        "استمتع بمباراة حماسية على ملعبنا القياسي. نوفر لك الشبكة والمعدات اللازمة.",
      descriptionEn:
        "Enjoy a high-energy match on our standard volleyball court. Net and equipment provided.",
      image: "/Sports/VolleyballCourt.jpeg",
    },
  ];

  const handleBooking = (id) => {
    navigate(`/activities/categories/${id}-schedule`);
  };

  if (loading) {
    return (
      <FullPageLoader loadingName="جاري تحضير الجداول... | Preparing Schedules..." />
    );
  }

  return (
    <div
      style={{ marginTop: "60px", minHeight: "100vh" }}
      className="sports-wrapper"
    >
      {/* Background Orbs */}
      <div className="background-wrapper">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
      </div>

      <div className="container position-relative z-2 pb-5">
        {/* Header Section - Bilingual */}
        <div className="text-center mb-5">
          <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
            <Sparkles size={24} className="text-info" />
            <span className="text-info fw-bold small tracking-wider text-uppercase">
              الحجز | Booking
            </span>
          </div>

          <div className="mb-4">
            <h1
              className="sports-title-gradient mb-1"
              dir="rtl"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
            >
              احجز ملعبك
            </h1>
            <h2
              className="sports-title-gradient"
              dir="ltr"
              style={{ fontSize: "clamp(1.4rem, 2.5vw, 1.8rem)", opacity: 0.7 }}
            >
              Reserve Your Court
            </h2>
          </div>

          <div
            className="mx-auto d-flex flex-column gap-2"
            style={{ maxWidth: "600px" }}
          >
            <p
              className="mb-0 fw-medium text-white px-2"
              dir="rtl"
              style={{ fontSize: "1.1rem" }}
            >
              اختر رياضتك المفضلة، تحقق من الأوقات المتاحة، واحجز ملعبك فوراً
              بكل سهولة.
            </p>
            <p
              className="mb-0 px-2"
              dir="ltr"
              style={{ color: "#94a3b8", fontSize: "0.95rem" }}
            >
              Select your preferred sport, check availability, and secure your
              playtime instantly.
            </p>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="row g-4 justify-content-center">
          {sportsPlaces.map((place) => (
            <div key={place.id} className="col-12 col-md-6 col-lg-4">
              <div className="sport-glass-card h-100 d-flex flex-column">
                {/* Image Component */}
                <SmoothImage src={place.image} alt={place.titleEn} />

                {/* Content Section - Bilingual */}
                <div className="sport-content d-flex flex-column flex-grow-1 gap-3 p-4 text-center">
                  {/* Titles */}
                  <div className="d-flex flex-column gap-1">
                    <h3
                      className="sport-title m-0 fw-bold"
                      dir="rtl"
                      style={{ color: "#fff", fontSize: "1.4rem" }}
                    >
                      {place.titleAr}
                    </h3>
                    <h4
                      className="m-0 fw-semibold"
                      dir="ltr"
                      style={{
                        color: "#cbd5e1",
                        fontSize: "1.1rem",
                        opacity: 0.8,
                      }}
                    >
                      {place.titleEn}
                    </h4>
                  </div>

                  {/* Descriptions */}
                  <div className="d-flex flex-column gap-2 flex-grow-1 mt-2">
                    <p
                      className="sport-desc m-0"
                      dir="rtl"
                      style={{
                        color: "#f8fafc",
                        fontSize: "1rem",
                        lineHeight: "1.6",
                      }}
                    >
                      {place.descriptionAr}
                    </p>
                    <hr className="m-0 border-secondary opacity-25 w-50 mx-auto my-1" />
                    <p
                      className="sport-desc m-0"
                      dir="ltr"
                      style={{
                        color: "#94a3b8",
                        fontSize: "0.9rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {place.descriptionEn}
                    </p>
                  </div>

                  {/* Action Button */}
                  <button
                    className="btn-schedule-advanced mt-3 d-flex flex-column align-items-center justify-content-center py-3 gap-1 w-100"
                    onClick={() => handleBooking(place.id)}
                    style={{ transition: "all 0.3s ease" }}
                  >
                    <span
                      className="fw-bold d-flex align-items-center gap-2"
                      style={{ fontSize: "1.1rem" }}
                    >
                      احجز الآن <ArrowRight size={18} />
                    </span>
                    <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>
                      Schedule Now
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ChatbotButton />
    </div>
  );
}

// import React, { useState, useContext } from "react";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../../assets/styles/Sports_Schedule.css";
// import { useNavigate } from "react-router-dom";
// import { AuthContext } from "../../context/AuthContext.js";
// import FullPageLoader from "../../components/ui/fullPageLoader.js";
// import ChatbotButton from "../../components/common/ChatbotButton";
// import {  Sparkles, ArrowRight } from "lucide-react";

// // --- 1. Smart Image Component (حل مشكلة الصور) ---
// const SmoothImage = ({ src, alt }) => {
//   const [isLoaded, setIsLoaded] = useState(false);

//   return (
//     <div className="sport-img-container">
//       {/* Skeleton Loader: يظهر طالما الصورة لسه متحملتش */}
//       {!isLoaded && <div className="img-skeleton"></div>}

//       <img
//         src={src}
//         alt={alt}
//         className={`sport-img-advanced ${isLoaded ? "loaded" : ""}`}
//         onLoad={() => setIsLoaded(true)} // لما تتحمل نخليها تظهر
//         loading="lazy" // تحسين الأداء
//       />
//     </div>
//   );
// };

// export default function Sports_Schedule() {
//   const navigate = useNavigate();
//   const { loading } = useContext(AuthContext);

//   const sportsPlaces = [
//     {
//       id: "football",
//       title: "Football Field",
//       description: "Reserve the professional turf field. Perfect for 5v5 matches with your squad.",
//       image: "/Sports/Football.jpeg",
//     },
//     {
//       id: "basketball",
//       title: "Basketball Hall",
//       description: "Book the indoor court with premium flooring. Ideal for competitive games or practice.",
//       image: "/Sports/BasketballHall.jpeg",
//     },
//     {
//       id: "volleyball",
//       title: "Volleyball Court",
//       description: "Enjoy a high-energy match on our standard volleyball court. Net and equipment provided.",
//       image: "/Sports/VolleyballCourt.jpeg",
//     },
//   ];

//   const handleBooking = (id) => {
//     navigate(`/activities/categories/${id}-schedule`);
//   };

//   if (loading) {
//     return <FullPageLoader loadingName="Preparing Schedules..." />;
//   }

//   return (
//     <div style={{marginTop:"60px"}} className="sports-wrapper">

//       {/* Background Orbs */}
//       <div className="background-wrapper">
//         <div className="orb orb-1"></div>
//         <div className="orb orb-2"></div>
//       </div>

//       <div className="container position-relative z-2">

//         {/* Header Section */}
//         <div className="text-center mb-5">
//           <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
//             <Sparkles size={24} className="text-info" />
//             <span className="text-info fw-bold small tracking-wider text-uppercase">Booking</span>
//           </div>
//           <h1 className="sports-title-gradient display-4 mb-3">
//             Reserve Your Court
//           </h1>
//           <p className="text-secondary mx-auto" style={{ maxWidth: "600px" }}>
//             Select your preferred sport, check availability, and secure your playtime instantly.
//           </p>
//         </div>

//         {/* Cards Grid */}
//         <div className="row g-4 justify-content-center">
//           {sportsPlaces.map((place) => (
//             <div key={place.id} className="col-12 col-md-6 col-lg-4">
//               <div className="sport-glass-card">

//                 {/* 2. استخدام كومبوننت الصور الذكي */}
//                 <SmoothImage src={place.image} alt={place.title} />

//                 <div className="sport-content">
//                   <h3 className="sport-title">{place.title}</h3>
//                   <p className="sport-desc">{place.description}</p>

//                   <button
//                     className="btn-schedule-advanced"
//                     onClick={() => handleBooking(place.id)}
//                   >
//                     Schedule Now <ArrowRight size={18} />
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>

//       <ChatbotButton />
//     </div>
//   );
// }
